import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import ReactECharts from 'echarts-for-react';
import axios from 'axios';
import LoadingSpinner from '../../Components/Common/LoadingSpinner';

const RevenueAnalytics = ({ dateRange, groupBy }) => {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState({
        labels: [],
        essential: [],
        premium: [],
        black: [],
        tokens: []
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const authToken = JSON.parse(sessionStorage.getItem('authUser'))?.token;

            if (!authToken) {
                console.error('Authentication token not found');
                return;
            }

            const response = await axios.get(`${process.env.REACT_APP_API_URL1}analytics/dashboard/revenue-by-plan`, {
                params: {
                    start_date: dateRange.start.toISOString(),
                    end_date: dateRange.end.toISOString(),
                    group_by: groupBy
                },
                headers: {
                    'Authorization': `${authToken}`
                }
            });

            if (response.data.series) {
                const series = response.data.series;
                //const { series } = response.data.series;
                setChartData({
                    labels: series.timestamps,
                    essential: series.essential_revenue,
                    premium: series.premium_revenue,
                    black: series.black_revenue,
                    tokens: series.token_revenue || []
                });
            }
            /*if (response.data.success) {
                const { series } = response.data.data;
                setChartData({
                    labels: series.timestamps,
                    essential: series.essential_revenue,
                    premium: series.premium_revenue,
                    black: series.black_revenue
                });
            }*/
        } catch (error) {
            console.error("Error fetching revenue data:", error);
            if (error.response) {
                console.error("Response data:", error.response.data);
                console.error("Response status:", error.response.status);
            }
        } finally {
            setLoading(false);
        }
    };

    // Listen for WebSocket updates
    useEffect(() => {
        const handleRevenueAnalyticsUpdate = (data) => {
            console.log('RevenueAnalytics component received update:', data);
            if (data && data.series) {
                const series = data.series;
                setChartData({
                    labels: series.timestamps || [],
                    essential: series.essential_revenue || [],
                    premium: series.premium_revenue || [],
                    black: series.black_revenue || [],
                    tokens: series.token_revenue || []
                });
                setLoading(false);
            }
        };
        
        // Add event listener
        if (window.dashboardEvents) {
            console.log('RevenueAnalytics - Setting up event listener for dashboard:revenueAnalytics');
            window.dashboardEvents.on('dashboard:revenueAnalytics', handleRevenueAnalyticsUpdate);
        } else {
            console.warn('RevenueAnalytics - window.dashboardEvents is not available');
        }
        
        // Cleanup function
        return () => {
            if (window.dashboardEvents) {
                window.dashboardEvents.off('dashboard:revenueAnalytics', handleRevenueAnalyticsUpdate);
            }
        };
    }, []);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 300000); // Update every 5 minutes
        return () => clearInterval(interval);
    }, [dateRange, groupBy]);

    const getOption = () => ({
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            },
            formatter: (params) => {
                let total = 0;
                let tooltipContent = `${params[0].axisValue}<br/>`;
                
                params.forEach(param => {
                    total += param.value;
                    tooltipContent += `${param.marker} ${param.seriesName}: $${param.value.toLocaleString()}<br/>`;
                });
                
                tooltipContent += `<strong>Total: $${total.toLocaleString()}</strong>`;
                return tooltipContent;
            }
        },
        legend: {
            data: ['Essential', 'Premium', 'Black', 'Tokens'],
            right: '4%'
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
        },
        xAxis: [
            {
                type: 'category',
                data: chartData.labels,
                axisLabel: {
                    formatter: (value) => {
                        // Remove the trailing Z and handle the +00:00 format
                        const cleanDate = value.replace('Z', '').replace('+00:00', '');
                        const date = new Date(cleanDate);
                        
                        if (isNaN(date.getTime())) {
                            console.error('Invalid date:', value);
                            return 'Invalid Date';
                        }

                        switch (groupBy) {
                            case 'daily':
                                return date.toLocaleDateString(undefined, { 
                                    month: 'short', 
                                    day: 'numeric' 
                                });
                            case 'weekly':
                                return `Week ${Math.ceil(date.getDate() / 7)} ${
                                    date.toLocaleDateString(undefined, { month: 'short' })
                                }`;
                            case 'monthly':
                                return date.toLocaleDateString(undefined, { 
                                    month: 'short', 
                                    year: 'numeric' 
                                });
                            case 'yearly':
                                return date.getFullYear().toString();
                            default:
                                return date.toLocaleDateString();
                        }
                    }
                }
            }
        ],
        yAxis: [
            {
                type: 'value',
                name: 'Revenue ($)',
                axisLabel: {
                    formatter: (value) => `$${value.toLocaleString()}`
                }
            }
        ],
        series: [
            {
                name: 'Essential',
                type: 'bar',
                barWidth: '20%',
                stack: 'total',
                itemStyle: {
                    color: '#6366f1'
                },
                data: chartData.essential
            },
            {
                name: 'Premium',
                type: 'bar',
                stack: 'total',
                itemStyle: {
                    color: '#10b981'
                },
                data: chartData.premium
            },
            {
                name: 'Black',
                type: 'bar',
                stack: 'total',
                itemStyle: {
                    color: '#f59e0b'
                },
                data: chartData.black
            },
            {
                name: 'Tokens',
                type: 'bar',
                stack: 'total',
                itemStyle: {
                    color: '#3b82f6'
                },
                data: chartData.tokens
            }
        ]
    });

    return (
        <Card>
            <CardHeader>
                <h4 className="card-title mb-0">Revenue by Plan</h4>
            </CardHeader>
            <CardBody>
                {loading ? (
                    <div className="text-center py-4">
                        <LoadingSpinner message="Loading revenue data..." />
                    </div>
                ) : (
                    <ReactECharts 
                        option={getOption()} 
                        className="header-chart" 
                        style={{ height: "350px" }}
                    />
                )}
            </CardBody>
        </Card>
    );
};

export default RevenueAnalytics; 